/**
 * CryptoContext — chain-scoped E2EE key management for web admin panels.
 *
 * Mirrors the mobile CryptoContext but uses:
 *   • IndexedDB (via @workspace/crypto) for non-extractable private key storage.
 *   • The same Firestore memberKeys subcollection for the chain key.
 *
 * Usage: wrap your app with <CryptoProvider> inside <AuthProvider>.
 * Consume with useCrypto() to get encrypt/decrypt helpers.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import {
  generateUserKeypair,
  exportPublicKey,
  storePrivateKey,
  loadPrivateKey,
  clearPrivateKey,
  storePublicKey,
  loadPublicKey,
  clearPublicKey,
  generateChainKey,
  wrapKeyForUser,
  validateBookingKeypair,
  generateBookingKeypairFields,
  unwrapKeyForUser,
  importPublicKey,
  encrypt,
  decrypt,
  encryptBytes,
  decryptBytes,
  wrapKeyWithPassphrase,
  unwrapKeyWithPassphrase,
} from "@workspace/crypto";
import { getFirebaseClient } from "./firebase";
import { useAuth } from "./auth";

// ── Types ────────────────────────────────────────────────────────────────

export type WebCryptoStatus =
  | "idle"
  | "loading"
  | "ready"
  | "awaiting-delivery"
  | "escrow-recovery"
  | "new-device"
  | "no-chain-key"
  | "error";

/**
 * Populated when status is "new-device" to give UI components enough
 * information to show actionable guidance instead of the generic
 * "could not be recovered" message.
 *
 *   "escrow-missing" — no encryptedMasterKey on the chain doc; manual
 *                      re-delivery by another admin is required.
 *   "timeout"        — escrow exists but delivery timed out (offer Retry
 *                      Recovery which calls requestChainKeyRedelivery).
 *   "error"          — unexpected error during recovery; Retry may help.
 */
export type WebCryptoRecoveryReason = "escrow-missing" | "timeout" | "error" | null;

export interface CryptoContextValue {
  status: WebCryptoStatus;
  /** Populated when status === "new-device"; null otherwise. */
  cryptoRecoveryReason: WebCryptoRecoveryReason;
  isReady: boolean;
  /**
   * Encrypt a UTF-8 string with the chain key.
   * Throws if the chain key is not loaded.
   */
  encryptString: (plaintext: string) => Promise<string>;
  /**
   * Decrypt a base64 ciphertext blob back to a UTF-8 string.
   * Throws if the chain key is not loaded.
   */
  decryptString: (ciphertext: string) => Promise<string>;
  /**
   * Decrypt raw bytes (nonce || ciphertext) produced by {@link encryptBytes}
   * back to plaintext bytes, using the chain key. Mirrors decryptString but
   * for binary payloads (e.g. consultation PDFs). Throws if the chain key is
   * not loaded.
   */
  decryptFileBytes: (data: Uint8Array) => Promise<Uint8Array>;
  /**
   * Wrap the chain key with an invite token (passphrase) for delivery.
   * Used by the invite creation flow.
   */
  wrapChainKeyForInvite: (inviteToken: string) => Promise<string>;
  /**
   * Store the chain key received via invite acceptance.
   */
  storeChainKeyFromInvite: (
    chainId: string,
    encryptedChainKey: string,
    inviteToken: string,
  ) => Promise<void>;
  /**
   * Wrap the chain key under a pharmacist's public RSA key and write it to
   * chains/{chainId}/memberKeys/{uid}.  Called from the chain-admin UI to
   * deliver the chain key to a directly-created pharmacist once they have
   * published their public key to users/{uid}.publicKey.
   *
   * Returns true on success, false if the pharmacist has not yet published
   * a public key (they haven't signed in on a device yet).
   */
  deliverChainKeyToMember: (
    memberUid: string,
    chainIdArg: string,
    pharmacyIdHint?: string,
  ) => Promise<boolean>;
  /** Re-run the init sequence (e.g. after invite acceptance). */
  reinit: () => Promise<void>;
  /**
   * Directly request chain-key re-delivery from the server escrow.
   * Returns the callable result ("delivered" | "escrow-missing" | "no-public-key").
   * Use this from the "Retry Recovery" button when status === "new-device".
   * Resumes polling for 15 s after a successful delivery.
   */
  requestRecovery: () => Promise<void>;
}

const noopCtx: CryptoContextValue = {
  status: "idle",
  cryptoRecoveryReason: null,
  isReady: false,
  encryptString: () => Promise.reject(new Error("CryptoProvider not mounted")),
  decryptString: () => Promise.reject(new Error("CryptoProvider not mounted")),
  decryptFileBytes: () => Promise.reject(new Error("CryptoProvider not mounted")),
  wrapChainKeyForInvite: () => Promise.reject(new Error("CryptoProvider not mounted")),
  storeChainKeyFromInvite: () => Promise.reject(new Error("CryptoProvider not mounted")),
  deliverChainKeyToMember: () => Promise.reject(new Error("CryptoProvider not mounted")),
  reinit: async () => {},
  requestRecovery: () => Promise.reject(new Error("CryptoProvider not mounted")),
};

const Ctx = createContext<CryptoContextValue>(noopCtx);

// ── Chain key escrow helpers ──────────────────────────────────────────────

/**
 * Fire-and-forget: export the chain key as raw bytes and store an
 * AES-256-GCM-encrypted escrow copy on chains/{chainId}.encryptedMasterKey
 * via the storeChainKeyEscrow Cloud Function callable.
 *
 * This lets the autoDeliverChainKey Firestore trigger deliver the chain key
 * to newly-invited pharmacists without admin intervention the moment they
 * publish their RSA public key.
 *
 * Retries up to 3 times with exponential back-off. On final failure, logs at
 * console.error so the broken recovery path is visible to developers/ops.
 */
async function ensureChainKeyEscrow(
  functions: ReturnType<typeof import("firebase/functions").getFunctions>,
  db: ReturnType<typeof import("firebase/firestore").getFirestore>,
  chainId: string,
  chainKey: CryptoKey,
): Promise<void> {
  const MAX_ATTEMPTS = 3;
  let lastErr: unknown;

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    try {
      // Only proceed if no escrow is stored yet.
      const chainSnap = await getDoc(doc(db, "chains", chainId));
      if (chainSnap.data()?.encryptedMasterKey) return;

      // Export raw AES-256 key bytes (generateChainKey uses extractable: true).
      const rawBuffer = await crypto.subtle.exportKey("raw", chainKey);
      const rawBytes = new Uint8Array(rawBuffer);
      // Base64-encode for JSON transport over the HTTPS callable (TLS-secured).
      const chunk = new Array<string>(rawBytes.length);
      for (let i = 0; i < rawBytes.length; i++) chunk[i] = String.fromCharCode(rawBytes[i]!);
      const rawChainKeyBase64 = btoa(chunk.join(""));

      const fn = httpsCallable(functions, "storeChainKeyEscrow");
      await fn({ chainId, rawChainKeyBase64 });
      return; // success
    } catch (err) {
      lastErr = err;
      if (attempt < MAX_ATTEMPTS - 1) {
        // Exponential back-off: 1 s, 2 s
        await new Promise<void>((resolve) =>
          setTimeout(resolve, 1000 * Math.pow(2, attempt)),
        );
      }
    }
  }

  // All attempts failed — log at error so the broken recovery path is visible.
  console.error(
    "[crypto] ensureChainKeyEscrow failed after",
    MAX_ATTEMPTS,
    "attempts — self-recovery will not work until escrow is stored:",
    lastErr,
  );
}

// ── Public key publish retry ───────────────────────────────────────────────

/**
 * Publish the public key to users/{uid}.publicKey, retrying transient
 * failures with bounded exponential back-off (mirrors ensureChainKeyEscrow
 * above).
 *
 * A single failed attempt here used to strand the user permanently: the
 * private key was already persisted in IndexedDB, so every later app open
 * found it and skipped straight past keypair generation without ever
 * retrying the Firestore write. The self-heal check in init() (below) closes
 * that gap by re-publishing whenever the server copy is missing or stale.
 */
async function publishPublicKeyWithRetry(
  db: ReturnType<typeof import("firebase/firestore").getFirestore>,
  uid: string,
  pubBase64: string,
): Promise<void> {
  const MAX_ATTEMPTS = 3;
  let lastErr: unknown;
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    try {
      await setDoc(
        doc(db, "users", uid),
        { publicKey: pubBase64, publicKeyUpdatedAt: serverTimestamp() },
        { merge: true },
      );
      return;
    } catch (err) {
      lastErr = err;
      if (attempt < MAX_ATTEMPTS - 1) {
        // Exponential back-off: 1 s, 2 s
        await new Promise<void>((resolve) => setTimeout(resolve, 1000 * Math.pow(2, attempt)));
      }
    }
  }
  throw lastErr instanceof Error
    ? lastErr
    : new Error("Failed to publish public key to Firestore after retries");
}

// ── Booking keypair helpers ───────────────────────────────────────────────

/**
 * Ensure the per-chain booking RSA keypair is present AND currently usable,
 * regenerating it when it is not.
 *
 * Public key is stored in plain on chains/{chainId}.bookingPublicKey so the
 * unauthenticated patient booking web app can encrypt before sending.
 * Private key is AES-encrypted with the chain key and stored in
 * chains/{chainId}.encryptedBookingPrivateKey — readable only by chain members.
 *
 * Previously this no-op'd whenever `bookingPublicKey` was merely present,
 * which meant a chain whose wrapped private key went missing (partial write)
 * or stopped unwrapping (chain-key rotation) stayed broken forever — the
 * public key kept working so the booking app kept encrypting to a key
 * nobody could ever read back. This now validates the *pair* against the
 * *current* chain key every time and, when it's not usable, regenerates and
 * rewrites both fields atomically (firestore.rules only allow chain admins
 * to update exactly these two fields together).
 *
 * Silently swallows errors so a keypair-generation failure never blocks the
 * normal chain-key init flow — the mobile app's own booking-key loader logs
 * a distinct reason code if the pair is still unusable afterwards.
 */
async function ensureBookingKeypair(
  db: ReturnType<typeof import("firebase/firestore").getFirestore>,
  chainId: string,
  chainKey: CryptoKey,
): Promise<void> {
  try {
    const chainSnap = await getDoc(doc(db, "chains", chainId));
    const data = chainSnap.data();
    const validity = await validateBookingKeypair(
      {
        bookingPublicKey: data?.bookingPublicKey,
        encryptedBookingPrivateKey: data?.encryptedBookingPrivateKey,
      },
      chainKey,
    );
    if (validity.valid) return; // already initialised and usable with the current chain key

    console.warn("[crypto] booking keypair unusable — regenerating", {
      chainId,
      reason: validity.reason,
    });

    const fields = await generateBookingKeypairFields(chainKey);
    await updateDoc(doc(db, "chains", chainId), fields);

    console.warn("[crypto] booking keypair rotated", {
      chainId,
      previousReason: validity.reason,
    });
  } catch (err) {
    console.warn("[crypto] booking keypair validate/regenerate failed (non-fatal):", err);
  }
}

// ── Provider ─────────────────────────────────────────────────────────────

export function CryptoProvider({ children }: { children: ReactNode }) {
  const { user, membership } = useAuth();
  const [status, setStatus] = useState<WebCryptoStatus>("idle");
  const [recoveryReason, setRecoveryReason] = useState<WebCryptoRecoveryReason>(null);
  const privateKeyRef = useRef<CryptoKey | null>(null);
  const chainKeyRef = useRef<CryptoKey | null>(null);
  const initDoneRef = useRef<string>(""); // tracks uid:chainId that was last init'd

  // Scoped per (uid, chainId) — see currentInitKeyRef below for why a single
  // shared boolean is wrong here. Two DIFFERENT chains can each legitimately
  // need to rotate the device's keypair around the same time (e.g. one
  // membership's slot is genuinely stale while the user is mid-switch to a
  // different membership); a single global flag would make the second one
  // hard-fail with "rotation already in progress" purely because of timing,
  // even though the two rotations have nothing to do with each other.
  const rotationInProgressRef = useRef<Set<string>>(new Set());

  // Identifies which (uid, chainId) pair the currently-authoritative init()
  // run is for. Every init() call — including recursive rotation retries —
  // claims ownership by writing its own initKey here as its very first act.
  //
  // Without this, a background poll/rotation loop started for a PREVIOUS
  // chainId (e.g. the membership the user just switched away from) keeps
  // running after the effect re-fires for the new chainId: React does not
  // cancel in-flight async work when effect deps change. Both runs share
  // the same setStatus/chainKeyRef/privateKeyRef/rotationInProgressRef, and
  // critically the same clearPrivateKey(uid)/clearPublicKey(uid) calls,
  // which are keyed ONLY by uid — not by chain. An abandoned old-chain run
  // that hits an unwrap failure (its own memberKeys slot was never, and
  // will never be, delivered) would otherwise wipe the device's keypair
  // and flip status back to "error"/"new-device" out from under whichever
  // chain is actually active now — a "hang"/false-failure the user did
  // nothing to cause. Every side-effecting step below checks isStale(...)
  // immediately after each await and bails out silently once it is no
  // longer the current run, so an abandoned run can observe state but
  // never mutate it.
  const currentInitKeyRef = useRef<string>("");
  const isStale = (initKey: string) => currentInitKeyRef.current !== initKey;

  // Unmount is just another way for a run to become stale, and nothing else
  // catches it: the delivery poll below sleeps up to a minute in total, so a
  // provider torn down mid-wait (sign-out, panel unmount, a page teardown)
  // leaves that loop alive against a component that no longer exists. It then
  // keeps reading Firestore and — once the slot lands — escrows the chain key
  // and calls setStatus on a dead tree, i.e. a backend write ordered by a
  // provider the user already left. Clearing the authoritative init key makes
  // isStale(...) true for every in-flight run, so each bails at its next
  // checkpoint instead.
  useEffect(() => () => { currentInitKeyRef.current = ""; }, []);

  const chainId = membership?.chainId ?? null;
  const uid = user?.uid ?? null;

  // ── Escrow-recovery helper ─────────────────────────────────────────────
  //
  // Called by the polling loops when the initial 30-poll window expires.
  // Invokes requestChainKeyRedelivery, then continues polling for 15 more
  // seconds to pick up the delivered key.  Returns true if the key was
  // loaded successfully (caller should return), false if the loop should
  // surface new-device.
  //
  // Preconditions: memberKeyRef and privateKey refer to the current chain/uid.
  const tryEscrowRecovery = useCallback(
    async (
      memberKeyRef: ReturnType<typeof doc>,
      privateKey: CryptoKey,
      initKey: string,
    ): Promise<boolean> => {
      if (!uid || !chainId) return false;
      if (isStale(initKey)) return true; // superseded — caller should return
      const { db, functions } = getFirebaseClient();

      setStatus("escrow-recovery");
      const fn = httpsCallable<
        { chainId: string },
        { ok: boolean; result: "delivered" | "escrow-missing" | "no-public-key" }
      >(functions, "requestChainKeyRedelivery");

      let escrowResult: "delivered" | "escrow-missing" | "no-public-key" | "error" = "error";
      try {
        const res = await fn({ chainId });
        escrowResult = res.data.result;
      } catch (err) {
        console.error("[crypto] requestChainKeyRedelivery failed:", err);
        escrowResult = "error";
      }
      if (isStale(initKey)) return true; // superseded while the callable was in flight

      if (escrowResult !== "delivered") {
        const reason: WebCryptoRecoveryReason =
          escrowResult === "escrow-missing" ? "escrow-missing" : "error";
        setRecoveryReason(reason);
        setStatus("new-device");
        return true; // handled — caller should return
      }

      // Escrow delivery succeeded — poll 15 more seconds to pick up the key.
      setStatus("awaiting-delivery");
      const MAX_RECOVERY_POLLS = 15;
      const POLL_INTERVAL_MS = 2000;
      for (let attempt = 0; attempt < MAX_RECOVERY_POLLS; attempt++) {
        await new Promise<void>((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        if (!uid || !chainId) return true;
        if (isStale(initKey)) return true; // superseded — caller should return
        try {
          const snap = await getDoc(memberKeyRef);
          if (snap.exists()) {
            const wrappedKey = snap.data()?.wrappedKey as string | undefined;
            if (wrappedKey && typeof wrappedKey === "string" && wrappedKey.length > 0) {
              const chainKey = await unwrapKeyForUser(wrappedKey, privateKey);
              if (isStale(initKey)) return true; // superseded mid-unwrap
              chainKeyRef.current = chainKey;
              await ensureBookingKeypair(db, chainId, chainKey);
              if (isStale(initKey)) return true; // superseded mid-await — don't finalize as ready
              initDoneRef.current = initKey;
              rotationInProgressRef.current.delete(initKey);
              setRecoveryReason(null);
              setStatus("ready");
              void ensureChainKeyEscrow(getFirebaseClient().functions, db, chainId, chainKey);
              return true;
            }
          }
        } catch (pollErr) {
          console.warn("[crypto] recovery poll attempt failed (non-fatal):", pollErr);
        }
      }
      if (isStale(initKey)) return true; // superseded — caller should return
      // Recovery polling timed out.
      setRecoveryReason("timeout");
      setStatus("new-device");
      return true;
    },
    [uid, chainId],
  );

  const init = useCallback(async () => {
    if (!uid || !chainId) {
      setStatus("idle");
      return;
    }
    const initKey = `${uid}:${chainId}`;
    if (initDoneRef.current === initKey && status === "ready") return;

    // Claim ownership of the shared refs/state for this (uid, chainId) pair.
    // Any previous run — e.g. a still-polling init() for a chain the user
    // just switched away from — is superseded from this point on; every
    // isStale(initKey) check below will now return true for it.
    currentInitKeyRef.current = initKey;

    setStatus("loading");
    setRecoveryReason(null);
    const { db } = getFirebaseClient();

    // ── 1. Ensure user keypair ───────────────────────────────────────
    //
    // First check whether the private key is already in IndexedDB.  If it is,
    // the user is re-polling on the same device (or the same browser session)
    // and we MUST NOT generate a new keypair — doing so would publish a new
    // public key to Firestore, invalidate any in-flight autoDeliverChainKey
    // delivery, and leave multiple racing deliveries for different keys.
    let privateKey = await loadPrivateKey(uid);
    if (!privateKey) {
      // Private key is NOT in IDB: either a new user (first sign-in) or a
      // true new-device / cleared-IndexedDB scenario.
      // Check whether a public key was already published from another device.
      let existingPublicKey = "";
      try {
        const userSnap = await getDoc(doc(db, "users", uid));
        existingPublicKey = (userSnap.data()?.publicKey as string | undefined) ?? "";
      } catch (err) {
        if (isStale(initKey)) return;
        console.error("[crypto] users doc read failed during new-device check", err);
        setStatus("error");
        return;
      }
      if (isStale(initKey)) return;

      // Generate a fresh keypair — new user or true new-device / key rotation.
      // (We only reach here when loadPrivateKey returned null, so this is safe.)
      try {
        // Web: extractable=false — key lives in IndexedDB as CryptoKey only.
        const kp = await generateUserKeypair(false);
        if (isStale(initKey)) return; // superseded — do not touch shared IDB/state
        privateKey = kp.privateKey;
        await storePrivateKey(uid, privateKey);
        if (isStale(initKey)) return; // superseded mid-write

        const pubBase64 = await exportPublicKey(kp.publicKey);
        if (isStale(initKey)) return;
        // Persist the public key locally too. Unlike mobile, the web private
        // key is non-extractable, so this is the ONLY way to re-derive the
        // exact matching public key later if the Firestore publish below
        // fails or never completes (no connectivity, tab closed mid-write).
        await storePublicKey(uid, pubBase64);
        if (isStale(initKey)) return;
        await publishPublicKeyWithRetry(db, uid, pubBase64);
        if (isStale(initKey)) return; // superseded — a newer run owns the state now

        // If a public key already existed, this is a key rotation (new device /
        // cleared IndexedDB with prior activity). The old memberKeys slot is
        // encrypted under the old key and is now unusable. Poll for
        // autoDeliverChainKey to write a fresh slot under the new public key,
        // then fall back to escrow-recovery if the trigger is too slow.
        if (existingPublicKey) {
          setStatus("awaiting-delivery");
          privateKeyRef.current = privateKey;
          const memberKeyRef = doc(db, "chains", chainId, "memberKeys", uid);
          const MAX_POLLS = 30;
          const POLL_INTERVAL_MS = 2000;
          for (let attempt = 0; attempt < MAX_POLLS; attempt++) {
            await new Promise<void>((resolve) =>
              setTimeout(resolve, POLL_INTERVAL_MS),
            );
            // Abort if the component has been unmounted / uid changed.
            if (!uid || !chainId) return;
            // Abort if a newer init() (e.g. the user switched chains) has
            // taken over — mutating shared state here would clobber it.
            if (isStale(initKey)) return;
            try {
              const snap = await getDoc(memberKeyRef);
              if (snap.exists()) {
                const wrappedKey = snap.data()?.wrappedKey as string | undefined;
                if (wrappedKey && typeof wrappedKey === "string" && wrappedKey.length > 0) {
                  const chainKey = await unwrapKeyForUser(wrappedKey, privateKey);
                  if (isStale(initKey)) return;
                  chainKeyRef.current = chainKey;
                  await ensureBookingKeypair(db, chainId, chainKey);
                  if (isStale(initKey)) return; // superseded mid-await — don't finalize as ready
                  initDoneRef.current = initKey;
                  rotationInProgressRef.current.delete(initKey);
                  setRecoveryReason(null);
                  setStatus("ready");
                  void ensureChainKeyEscrow(getFirebaseClient().functions, db, chainId, chainKey);
                  return;
                }
              }
            } catch (pollErr) {
              console.warn("[crypto] poll attempt failed (non-fatal):", pollErr);
            }
          }
          if (isStale(initKey)) return;
          // Polling timed out — attempt escrow-recovery before surfacing
          // the manual-retry alert, so that CF cold-start delays don't
          // permanently block the user.
          const handled = await tryEscrowRecovery(memberKeyRef, privateKey, initKey);
          if (handled) return;
          if (isStale(initKey)) return;
          setRecoveryReason("timeout");
          setStatus("new-device");
          return;
        }
      } catch (err) {
        console.error("[crypto] keypair generation failed", err);
        setStatus("error");
        return;
      }
    } else {
      // Self-healing (step 1): a local private key alone doesn't guarantee
      // the server has the matching public key — see the retry-helper doc
      // comment above. Re-check against the locally-stored companion public
      // key (written alongside the private key at generation time) and
      // republish with retry ONLY if the server copy is missing.
      //
      // A server copy that is merely DIFFERENT is left alone. users/{uid}
      // .publicKey holds one key for the whole account, and the same person
      // can be in this panel while their phone runs the mobile app. Every
      // publish fires autoDeliverChainKey path A, which re-wraps the
      // memberKeys slot under the republisher's key and locks the other
      // surface out — so republishing on mere difference had the two surfaces
      // taking the slot from each other on every sign-in, indefinitely.
      //
      // If this device's key can still unwrap the slot there is nothing to
      // heal. If it cannot, the unwrap-failure branch below already rotates
      // and re-publishes, which takes the slot over once, deliberately,
      // rather than pre-emptively every time.
      //
      // DRIFT WARNING: the mobile CryptoContext (ensureKeypair's resync check)
      // implements the same publish-on-missing / take-over-on-failure rule.
      // The two are one policy — change them together.
      try {
        const localPublicKey = await loadPublicKey(uid);
        if (localPublicKey && !isStale(initKey)) {
          const userSnap = await getDoc(doc(db, "users", uid));
          if (!isStale(initKey)) {
            const serverPublicKey = userSnap.data()?.publicKey as string | undefined;
            if (!serverPublicKey) {
              console.warn(
                "[crypto] server publicKey missing for existing local keypair — publishing",
              );
              await publishPublicKeyWithRetry(db, uid, localPublicKey);
            } else if (serverPublicKey !== localPublicKey) {
              console.warn(
                "[crypto] server publicKey belongs to another device — deferring to it until an unwrap actually fails",
              );
            }
          }
        }
      } catch (err) {
        // Non-fatal — the user can keep using the app with the chain key
        // already loaded; worst case an admin needs to retry "Deliver key".
        console.warn("[crypto] public key re-sync check failed (non-fatal):", err);
      }
    }
    if (isStale(initKey)) return;
    privateKeyRef.current = privateKey;

    // ── 2. Load chain key from memberKeys (loadChainKey) ─────────────
    //
    // The chain AES key is stored ONLY as an RSA-OAEP-2048 ciphertext blob
    // in chains/{chainId}/memberKeys/{uid}.wrappedKey. It is NEVER stored in
    // plaintext — not on chains/{chainId} (world-readable) nor anywhere else
    // in Firestore. A Firebase console admin reading this document sees only
    // the RSA ciphertext; decryption requires the private key stored in IDB.
    const memberKeyRef = doc(db, "chains", chainId, "memberKeys", uid);
    let memberKeySnap;
    try {
      memberKeySnap = await getDoc(memberKeyRef);
    } catch (err) {
      // Permission-denied on a missing slot (Bug 1 race) or transient network
      // error — treat the same as "slot does not exist" and fall through to
      // step 3 (bootstrap or awaiting-delivery). Do NOT surface "error".
      console.warn("[crypto] memberKeys read failed — treating as absent slot", err);
      memberKeySnap = null;
    }

    if (memberKeySnap !== null && memberKeySnap.exists()) {
      if (isStale(initKey)) return; // superseded while reading the slot
      const wrappedKey = memberKeySnap.data()?.wrappedKey as string | undefined;
      if (!wrappedKey || typeof wrappedKey !== "string" || wrappedKey.length === 0) {
        // Slot exists but wrappedKey is missing/corrupt — trigger key rotation
        // so autoDeliverChainKey re-delivers under the current public key.
        console.warn("[crypto] memberKeys slot has missing or invalid wrappedKey — triggering rotation");
        // A superseded run must never touch the device's shared keypair —
        // clearPrivateKey/clearPublicKey are keyed by uid alone (not by
        // chain), so an abandoned old-chain run "rotating" here would wipe
        // out the keypair the now-current chain's already-ready session
        // depends on. Check staleness BEFORE any clearing, not after.
        if (isStale(initKey)) return;
        if (rotationInProgressRef.current.has(initKey)) {
          console.error("[crypto] key rotation already in progress — giving up to prevent recursion");
          setStatus("error");
          return;
        }
        rotationInProgressRef.current.add(initKey);
        try { await clearPrivateKey(uid); } catch { /* best-effort */ }
        try { await clearPublicKey(uid); } catch { /* best-effort */ }
        if (isStale(initKey)) {
          // Superseded mid-clear — a newer run now owns the device keypair
          // state; do not touch initDoneRef/rotation flags for it, and do
          // not recurse into a stale init().
          rotationInProgressRef.current.delete(initKey);
          return;
        }
        privateKeyRef.current = null;
        initDoneRef.current = "";
        await init();
        rotationInProgressRef.current.delete(initKey);
        return;
      }
      try {
        const chainKey = await unwrapKeyForUser(wrappedKey, privateKey);
        if (isStale(initKey)) return; // superseded mid-unwrap
        chainKeyRef.current = chainKey;
        // Ensure the chain's booking keypair exists (generates if absent).
        await ensureBookingKeypair(db, chainId, chainKey);
        if (isStale(initKey)) return;
        initDoneRef.current = initKey;
        rotationInProgressRef.current.delete(initKey);
        setRecoveryReason(null);
        setStatus("ready");
        // Background: store escrow copy so the trigger can auto-deliver to future
        // pharmacists without admin intervention. Fire-and-forget — never blocks UI.
        void ensureChainKeyEscrow(getFirebaseClient().functions, db, chainId, chainKey);
      } catch (err) {
        if (isStale(initKey)) return; // superseded — not a real failure for this run
        console.warn(
          "[crypto] chain key unwrap failed — IDB key may be stale; triggering key rotation",
          err,
        );
        // Same staleness guard as above: never clear the shared device
        // keypair on behalf of a superseded run.
        if (isStale(initKey)) return;
        if (rotationInProgressRef.current.has(initKey)) {
          console.error("[crypto] key rotation already in progress — giving up to prevent recursion");
          setStatus("error");
          return;
        }
        rotationInProgressRef.current.add(initKey);
        try { await clearPrivateKey(uid); } catch { /* best-effort */ }
        try { await clearPublicKey(uid); } catch { /* best-effort */ }
        if (isStale(initKey)) {
          rotationInProgressRef.current.delete(initKey);
          return;
        }
        privateKeyRef.current = null;
        initDoneRef.current = "";
        await init();
        rotationInProgressRef.current.delete(initKey);
        return;
      }
      return;
    }

    // ── 3. Bootstrap or await delivery ───────────────────────────────
    //
    // No memberKeys slot exists yet (or the read failed and was treated as absent).
    //
    // Chain admins (membership has no pharmacyId) may bootstrap a fresh chain
    // key and write their own slot — but ONLY when the chain genuinely has no
    // established key yet (see chainHasEstablishedKey check below).
    //
    // Pharmacy admins and pharmacists must NOT bootstrap — they should never
    // hold a chain-key-generation privilege. Instead they enter
    // "awaiting-delivery" and poll for autoDeliverChainKey to write their slot,
    // with an escrow-recovery fallback after the initial polling window.
    const isChainAdmin = !membership?.pharmacyId;

    // A chain admin with no memberKeys slot yet is normally the very first
    // admin of a brand-new chain — bootstrapping a fresh key is correct
    // there. But a SECOND (or later) chain admin — e.g. a katana_admin
    // granted an additional pharmacy_chain_admin membership on a chain that
    // already has real data — must never invent a competing key: the chain
    // may already have an escrowed key (chains/{chainId}.encryptedMasterKey)
    // that this admin simply hasn't received a memberKeys slot for yet
    // (server-side grant delivery may have raced ahead of this admin's
    // public key being published, or predates this fix). Check for an
    // established key first and, if present, follow the exact same
    // wait-for-delivery/escrow-recovery path pharmacy admins use instead of
    // self-generating.
    let chainHasEstablishedKey = false;
    if (isChainAdmin) {
      try {
        const chainSnap = await getDoc(doc(db, "chains", chainId));
        chainHasEstablishedKey = !!chainSnap.data()?.encryptedMasterKey;
      } catch (err) {
        // Non-fatal — if we can't tell, fall through to the normal chain-admin
        // bootstrap path below (same behavior as before this check existed).
        console.warn("[crypto] chain doc read failed while checking for an established key (non-fatal):", err);
      }
      if (isStale(initKey)) return; // superseded while reading the chain doc
    }

    if (!isChainAdmin || chainHasEstablishedKey) {
      // Pharmacy admin / pharmacist: wait for autoDeliverChainKey delivery.
      // The memberKeys read above was awaited, and a pharmacy admin skips the
      // chain-doc guard, so this is the first checkpoint on that path.
      if (isStale(initKey)) return;
      setStatus("awaiting-delivery");
      const MAX_POLLS = 30;
      const POLL_INTERVAL_MS = 2000;
      for (let attempt = 0; attempt < MAX_POLLS; attempt++) {
        await new Promise<void>((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        if (!uid || !chainId) return;
        if (isStale(initKey)) return; // superseded — e.g. user switched chains
        try {
          const snap = await getDoc(memberKeyRef);
          if (snap.exists()) {
            const wrappedKey = snap.data()?.wrappedKey as string | undefined;
            if (wrappedKey && typeof wrappedKey === "string" && wrappedKey.length > 0) {
              const chainKey = await unwrapKeyForUser(wrappedKey, privateKey);
              if (isStale(initKey)) return;
              chainKeyRef.current = chainKey;
              await ensureBookingKeypair(db, chainId, chainKey);
              if (isStale(initKey)) return; // superseded mid-await — don't finalize as ready
              initDoneRef.current = initKey;
              rotationInProgressRef.current.delete(initKey);
              setRecoveryReason(null);
              setStatus("ready");
              void ensureChainKeyEscrow(getFirebaseClient().functions, db, chainId, chainKey);
              return;
            }
          }
        } catch (pollErr) {
          console.warn("[crypto] delivery poll attempt failed (non-fatal):", pollErr);
        }
      }
      if (isStale(initKey)) return;
      // Polling timed out — attempt escrow-recovery before surfacing the
      // manual-retry alert, so CF cold-start delays don't permanently block.
      const handled = await tryEscrowRecovery(memberKeyRef, privateKey, initKey);
      if (handled) return;
      if (isStale(initKey)) return;
      setRecoveryReason("timeout");
      setStatus("new-device");
      return;
    }

    // Chain admin bootstrap: generate a fresh AES-256-GCM chain key and wrap
    // it under this admin's RSA-OAEP-2048 public key before touching Firestore.
    // The raw CryptoKey lives only in memory; Firestore receives only the RSA
    // ciphertext (wrappedKey), never the raw key.
    try {
      const userPublicKeyBase64 = (await getDoc(doc(db, "users", uid))).data()?.publicKey as string | undefined;
      if (isStale(initKey)) return;
      if (!userPublicKeyBase64) {
        setStatus("no-chain-key");
        return;
      }
      const pubKey = await importPublicKey(userPublicKeyBase64);
      const chainKey = await generateChainKey();
      const wrappedKey = await wrapKeyForUser(chainKey, pubKey);

      // Defensive guard: wrapKeyForUser must return a non-empty base64 string.
      if (!wrappedKey || typeof wrappedKey !== "string" || wrappedKey.length === 0) {
        throw new Error("wrapKeyForUser returned an empty blob — refusing to store");
      }
      if (isStale(initKey)) return;

      await setDoc(memberKeyRef, {
        wrappedKey,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      if (isStale(initKey)) return;
      chainKeyRef.current = chainKey;
      await ensureBookingKeypair(db, chainId, chainKey);
      if (isStale(initKey)) return; // superseded mid-await — don't finalize as ready
      initDoneRef.current = initKey;
      rotationInProgressRef.current.delete(initKey);
      setStatus("ready");
      void ensureChainKeyEscrow(getFirebaseClient().functions, db, chainId, chainKey);
    } catch (err) {
      if (isStale(initKey)) return;
      console.error("[crypto] chain key bootstrap failed", err);
      setStatus("no-chain-key");
    }
  }, [uid, chainId, membership, status, tryEscrowRecovery]);

  // Re-run init whenever auth state / membership changes.
  useEffect(() => {
    if (!uid || !chainId) {
      setStatus("idle");
      chainKeyRef.current = null;
      return;
    }
    const initKey = `${uid}:${chainId}`;
    if (initDoneRef.current === initKey && status === "ready") return;
    init();
  }, [uid, chainId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Proactive escrow repair.
  //
  // Escrow (chains/{chainId}.encryptedMasterKey) is what lets the server
  // deliver the chain key to a pharmacist who never received one. Individual
  // ready-paths below each call ensureChainKeyEscrow, but that made escrow a
  // side effect of *how* this session happened to obtain the key — a chain
  // predating the mechanism, or an admin who took a code path that skipped
  // it (or simply hit init()'s already-ready fast path), could stay without
  // escrow forever, stranding every pharmacist in that chain.
  //
  // So: any admin session that holds the chain key repairs escrow, full
  // stop. ensureChainKeyEscrow is idempotent — it no-ops when escrow already
  // exists — so the common case is one extra chain-doc read per sign-in.
  useEffect(() => {
    if (status !== "ready" || !chainId) return;
    const chainKey = chainKeyRef.current;
    if (!chainKey) return;
    const { db, functions } = getFirebaseClient();
    void ensureChainKeyEscrow(functions, db, chainId, chainKey);
  }, [status, chainId]);

  // Clean up when the user signs out.
  useEffect(() => {
    if (!uid && status !== "idle") {
      setStatus("idle");
      chainKeyRef.current = null;
      privateKeyRef.current = null;
    }
  }, [uid, status]);

  const encryptString = useCallback(
    (plaintext: string) => {
      if (!chainKeyRef.current) throw new Error("Chain key not loaded");
      return encrypt(plaintext, chainKeyRef.current);
    },
    [],
  );

  const decryptString = useCallback(
    (ciphertext: string) => {
      if (!chainKeyRef.current) throw new Error("Chain key not loaded");
      return decrypt(ciphertext, chainKeyRef.current);
    },
    [],
  );

  const decryptFileBytes = useCallback(
    (data: Uint8Array) => {
      if (!chainKeyRef.current) throw new Error("Chain key not loaded");
      return decryptBytes(data, chainKeyRef.current);
    },
    [],
  );

  const wrapChainKeyForInvite = useCallback(
    (inviteToken: string) => {
      if (!chainKeyRef.current) throw new Error("Chain key not loaded");
      return wrapKeyWithPassphrase(chainKeyRef.current, inviteToken);
    },
    [],
  );

  const storeChainKeyFromInvite = useCallback(
    async (chainIdArg: string, encryptedChainKey: string, inviteToken: string) => {
      const chainKey = await unwrapKeyWithPassphrase(encryptedChainKey, inviteToken);
      chainKeyRef.current = chainKey;

      const { db } = getFirebaseClient();
      const currentUid = uid;
      if (!currentUid || !privateKeyRef.current) return;

      const userSnap = await getDoc(doc(db, "users", currentUid));
      const pubBase64 = userSnap.data()?.publicKey as string | undefined;
      if (!pubBase64) return;

      const pubKey = await importPublicKey(pubBase64);
      const wrappedKey = await wrapKeyForUser(chainKey, pubKey);

      // Defensive guard: ensure the RSA-wrapped blob is non-empty before
      // writing to Firestore. The raw chain key must never reach Firestore.
      if (!wrappedKey || typeof wrappedKey !== "string" || wrappedKey.length === 0) {
        throw new Error("wrapKeyForUser returned an empty blob — refusing to store");
      }

      await setDoc(doc(db, "chains", chainIdArg, "memberKeys", currentUid), {
        wrappedKey,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      initDoneRef.current = `${currentUid}:${chainIdArg}`;
      setStatus("ready");
    },
    [uid],
  );

  const deliverChainKeyToMember = useCallback(
    async (memberUid: string, chainIdArg: string, pharmacyIdHint?: string): Promise<boolean> => {
      if (!chainKeyRef.current) throw new Error("Chain key not loaded");
      const { db, functions } = getFirebaseClient();

      // Fetch the member's public key via a server-side callable so that the
      // chain admin does not need direct read access to users/{uid} (which
      // Firestore rules restrict to the document owner).
      // The callable also returns pharmacyId from the matched membership doc,
      // avoiding a second client-side composite query (which required an index
      // and could silently fail, leaving pharmacyId empty in the written slot).
      const getPublicKeyFn = httpsCallable<
        { chainId: string; memberUid: string },
        { publicKey: string | null; pharmacyId: string; reason?: string }
      >(functions, "getChainMemberPublicKey");

      let result: { data: { publicKey: string | null; pharmacyId: string; reason?: string } };
      try {
        result = await getPublicKeyFn({ chainId: chainIdArg, memberUid });
      } catch (err) {
        // Surface the server's structured reason (e.g. "chain-mismatch",
        // "no-active-membership") to the browser console so whoever is
        // debugging a "hasn't synced" report doesn't have to guess.
        const httpsErr = err as { code?: string; message?: string; details?: unknown };
        console.error(
          "[crypto] deliverChainKeyToMember: getChainMemberPublicKey failed",
          {
            memberUid,
            chainId: chainIdArg,
            code: httpsErr.code,
            message: httpsErr.message,
            details: httpsErr.details,
          },
        );
        throw err;
      }

      const pubBase64 = result.data.publicKey ?? undefined;
      if (!pubBase64) {
        // Member hasn't generated their keypair yet (hasn't signed in on
        // device) — this is the case that surfaces as the "hasn't synced to
        // the server yet" toast. Log the specific server-side reason
        // (e.g. "no-user-doc" vs "empty-public-key") alongside the member's
        // uid/chainId so the next investigation has evidence, not a guess.
        console.warn(
          "[crypto] deliverChainKeyToMember: member has no public key yet",
          {
            memberUid,
            chainId: chainIdArg,
            reason: result.data.reason ?? "unknown",
          },
        );
        return false;
      }

      // pharmacyId is required by the memberKeys read rule so the pharmacist
      // (as document owner) can read their own key slot.
      // Prefer the hint if provided (re-deliver path), otherwise use the value
      // returned by the callable (which pulled it from the membership doc server-side).
      const pharmacyId = pharmacyIdHint || result.data.pharmacyId || "";

      // Refuse to write a slot the recipient's own read rule would deny.
      //
      // The memberKeys owner-read rule requires a non-empty pharmacyId that
      // names a pharmacy they are a member or admin of. Writing an empty one
      // "succeeds" at the Firestore level and then fails silently forever:
      // the panel reports the key delivered, the roster badge flips to
      // delivered, and the recipient still sees "[Encrypted]" with nothing
      // left for an admin to click. Returning false instead routes the admin
      // to the actionable warning — the same one an unpublished public key
      // produces — and leaves the slot absent so a later, correct delivery
      // (or the autoDeliverChainKey trigger) can still write it.
      if (!pharmacyId) {
        console.warn(
          "[crypto] deliverChainKeyToMember: no pharmacyId resolved — refusing to write a slot the read rule would deny",
          { memberUid, chainId: chainIdArg },
        );
        return false;
      }

      const memberPubKey = await importPublicKey(pubBase64);
      const wrappedKey = await wrapKeyForUser(chainKeyRef.current, memberPubKey);

      // Defensive guard: RSA-wrapped blob must be non-empty before Firestore write.
      if (!wrappedKey || typeof wrappedKey !== "string" || wrappedKey.length === 0) {
        throw new Error("wrapKeyForUser returned an empty blob — refusing to deliver key");
      }

      await setDoc(
        doc(db, "chains", chainIdArg, "memberKeys", memberUid),
        {
          wrappedKey,
          pharmacyId,
          deliveredByUid: uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
      );
      return true;
    },
    [uid],
  );

  const reinit = useCallback(async () => {
    // Explicit IDB check before resetting state: if the private key is still
    // in IndexedDB (user is re-polling on the same browser, not a true
    // new-device), we must NOT generate a new keypair — doing so would publish
    // a new public key and invalidate any in-flight delivery.  The existing
    // init() already skips generation when loadPrivateKey returns non-null, so
    // calling init() directly after resetting flags is sufficient.
    rotationInProgressRef.current.clear();
    initDoneRef.current = "";
    setRecoveryReason(null);
    setStatus("idle");
    await init();
  }, [init]);

  /**
   * Directly invoke the server escrow-recovery callable and resume polling.
   * Designed for the "Retry Recovery" button shown when status === "new-device".
   * Safe to call multiple times — idempotent on the server side.
   */
  const requestRecovery = useCallback(async () => {
    if (!uid || !chainId) return;
    const { db } = getFirebaseClient();
    const initKey = `${uid}:${chainId}`;
    const privateKey = privateKeyRef.current ?? (await loadPrivateKey(uid));
    if (!privateKey) {
      // Private key not in IDB — must do a full reinit (will generate a new keypair).
      await reinit();
      return;
    }
    privateKeyRef.current = privateKey;
    const memberKeyRef = doc(db, "chains", chainId, "memberKeys", uid);
    await tryEscrowRecovery(memberKeyRef, privateKey, initKey);
  }, [uid, chainId, tryEscrowRecovery, reinit]);

  return (
    <Ctx.Provider
      value={{
        status,
        cryptoRecoveryReason: recoveryReason,
        isReady: status === "ready",
        encryptString,
        decryptString,
        decryptFileBytes,
        wrapChainKeyForInvite,
        storeChainKeyFromInvite,
        deliverChainKeyToMember,
        reinit,
        requestRecovery,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useCrypto(): CryptoContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useCrypto must be inside <CryptoProvider>");
  return v;
}

/** Remove the private key (and its companion public key) from IDB for the given uid (on sign-out). */
export async function clearWebCryptoKey(uid: string): Promise<void> {
  await clearPrivateKey(uid);
  await clearPublicKey(uid);
}
